import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AdminPage } from "@/components/admin/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { PdfViewer } from "@/components/admin/PdfViewer";
import { Eye, GraduationCap } from "lucide-react";

export const Route = createFileRoute("/admin/academia")({
  head: () => ({
    meta: [{ title: "Academia Bizaclia — Admin" }, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: AcademiaPage,
});

type Clase = {
  id: string;
  titulo: string;
  descripcion: string | null;
  pdf_url: string;
  orden: number;
};

type Modulo = {
  id: string;
  numero: number;
  titulo: string | null;
  descripcion: string | null;
  clases: Clase[];
};

function AcademiaPage() {
  const [abierta, setAbierta] = useState<Clase | null>(null);

  const q = useQuery({
    queryKey: ["academia-modulos"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("academia_modulos")
        .select(
          "id, numero, titulo, descripcion, academia_clases(id, titulo, descripcion, pdf_url, orden)",
        )
        .order("numero", { ascending: true });
      if (error) throw error;
      return (data ?? [])
        .map(({ academia_clases, ...m }) => ({
          ...m,
          clases: [...(academia_clases ?? [])].sort((a, b) => a.orden - b.orden),
        }))
        .filter((m) => m.clases.length > 0) as Modulo[];
    },
  });

  return (
    <AdminPage
      title="Academia Bizaclia"
      description="Formación para agentes: módulos y unidades en PDF. Haz clic en una clase para verla."
    >
      {q.error ? (
        <p className="text-sm text-destructive">
          No se pudieron cargar las clases: {(q.error as Error).message}
        </p>
      ) : null}

      {q.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-3/4" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-10">
          {(q.data ?? []).map((m) => (
            <section key={m.id} aria-labelledby={`modulo-${m.numero}`} className="space-y-4">
              <header className="space-y-1 border-b pb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Módulo {m.numero} · {m.clases.length}{" "}
                  {m.clases.length === 1 ? "unidad" : "unidades"}
                </p>
                <h2 id={`modulo-${m.numero}`} className="text-lg font-semibold leading-snug">
                  {m.titulo ?? `Módulo ${m.numero}`}
                </h2>
                {m.descripcion && <p className="text-sm text-muted-foreground">{m.descripcion}</p>}
              </header>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {m.clases.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setAbierta(c)}
                    className="group block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
                  >
                    <Card className="h-full transition-shadow group-hover:shadow-md group-hover:border-primary/40">
                      <CardHeader className="pb-2">
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <GraduationCap className="h-5 w-5" />
                          </div>
                          <CardTitle className="text-base leading-snug">{c.titulo}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {c.descripcion && (
                          <p className="text-sm text-muted-foreground">{c.descripcion}</p>
                        )}
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                          <Eye className="h-3.5 w-3.5" />
                          Ver clase
                        </span>
                      </CardContent>
                    </Card>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {!q.isLoading && !q.error && (q.data ?? []).length === 0 && (
        <p className="text-sm text-muted-foreground">Todavía no hay clases publicadas.</p>
      )}

      <Dialog open={!!abierta} onOpenChange={(v) => !v && setAbierta(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="pr-8 text-base">{abierta?.titulo}</DialogTitle>
          </DialogHeader>
          {abierta && <PdfViewer url={abierta.pdf_url} title={abierta.titulo} />}
          <p className="text-xs text-muted-foreground">
            Contenido formativo de uso interno. Solo lectura.
          </p>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
